const DENOMINATORS = [1, 2, 3, 4, 5, 6, 8, 10, 12];
// Tolerancja musi przetrwać konwersję do float32 po stronie API (26/3 wraca jako 8.666667),
// a jednocześnie być mniejsza niż połowa najmniejszego odstępu między ułamkami (1/132).
const RELATIVE_EPS = 1e-5;
const MAX_EPS = 0.002;

interface Fraction {
    n: number;
    d: number;
}

export const AMOUNT_HINT = 'np. 1, 1/2, 1 1/3';

// Znaki ułamków zwykłych - dostępne tylko dla części kombinacji, reszta wraca do zapisu n/d.
const GLYPHS: Record<string, string> = {
    '1/2': '½',
    '1/3': '⅓',
    '2/3': '⅔',
    '1/4': '¼',
    '3/4': '¾',
    '1/5': '⅕',
    '2/5': '⅖',
    '3/5': '⅗',
    '4/5': '⅘',
    '1/6': '⅙',
    '5/6': '⅚',
    '1/8': '⅛',
    '3/8': '⅜',
    '5/8': '⅝',
    '7/8': '⅞',
    '1/10': '⅒',
};

const GLYPH_VALUES: Record<string, string> = Object.fromEntries(
    Object.entries(GLYPHS).map(([fraction, glyph]) => [glyph, fraction]),
);

const AMOUNT_PATTERN = /^(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)$/;
const DECIMAL_PATTERN = /^\d+(?:[.,]\d+)?$/;

function gcd(a: number, b: number): number {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y !== 0) {
        [x, y] = [y, x % y];
    }
    return x === 0 ? 1 : x;
}

export function toFraction(value: number): Fraction | undefined {
    if (!isFinite(value)) return undefined;
    const tolerance = Math.min(RELATIVE_EPS * Math.max(1, Math.abs(value)), MAX_EPS);
    for (const d of DENOMINATORS) {
        const scaled = value * d;
        const rounded = Math.round(scaled);
        if (Math.abs(scaled - rounded) < tolerance * d) {
            const divisor = gcd(rounded, d);
            return { n: rounded / divisor, d: d / divisor };
        }
    }
    return undefined;
}

function expandGlyphs(raw: string): string {
    return Object.entries(GLYPH_VALUES).reduce(
        (text, [glyph, fraction]) => text.split(glyph).join(` ${fraction}`),
        raw,
    );
}

export function parseAmount(raw: string): number | undefined {
    const text = expandGlyphs(raw).trim().replace(/\s+/g, ' ');
    if (!text) return undefined;

    const fractionMatch = AMOUNT_PATTERN.exec(text);
    if (fractionMatch) {
        const [, wholePart, numerator, denominator] = fractionMatch;
        const d = parseInt(denominator, 10);
        if (d === 0) return undefined;
        const whole = wholePart ? parseInt(wholePart, 10) : 0;
        return whole + parseInt(numerator, 10) / d;
    }

    if (DECIMAL_PATTERN.test(text)) {
        const value = parseFloat(text.replace(',', '.'));
        return isNaN(value) ? undefined : value;
    }

    return undefined;
}

function formatDecimal(value: number): string {
    return parseFloat(value.toFixed(2)).toString();
}

function format(value: number | undefined | null, useGlyphs: boolean): string {
    if (value === undefined || value === null || isNaN(value)) return '';

    const sign = value < 0 ? '-' : '';
    const absolute = Math.abs(value);
    const fraction = toFraction(absolute);
    if (!fraction) return sign + formatDecimal(absolute);

    const whole = Math.floor(fraction.n / fraction.d);
    const remainder = fraction.n - whole * fraction.d;
    if (remainder === 0) return `${sign}${whole}`;

    const asciiPart = `${remainder}/${fraction.d}`;
    const glyph = useGlyphs ? GLYPHS[asciiPart] : undefined;
    const fractionPart = glyph ?? asciiPart;
    if (whole === 0) return `${sign}${fractionPart}`;
    // Znak ułamka przykleja się do liczby całkowitej (1½), zapis n/d wymaga odstępu (1 1/12).
    return `${sign}${whole}${glyph ? '' : ' '}${fractionPart}`;
}

/** Zapis edytowalny, zgodny z tym, co przyjmuje parseAmount (np. "1 1/3"). */
export function formatAmount(value: number | undefined | null): string {
    return format(value, false);
}

/** Zapis do wyświetlenia, ze znakami ułamków tam, gdzie istnieją (np. "1⅓"). */
export function formatAmountForDisplay(value: number | undefined | null): string {
    return format(value, true);
}

export function roundAmount(value: number): number {
    const fraction = toFraction(value);
    if (!fraction) return Number(value.toFixed(6));
    return fraction.n / fraction.d;
}

export function subtractAmount(a: number, b: number): number {
    const left = toFraction(a);
    const right = toFraction(b);
    if (!left || !right) return Number((a - b).toFixed(6));

    const denominator = (left.d * right.d) / gcd(left.d, right.d);
    const numerator = left.n * (denominator / left.d) - right.n * (denominator / right.d);
    return numerator / denominator;
}
